import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import axiosInstance, { setAccessToken as setAxiosToken, initializeCsrf, setRefreshFunction } from '../api/axiosInstance';
import {jwtDecode} from 'jwt-decode';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

const AuthContext = createContext();
export const accessTokenRef = { current: null };

// Helper to detect if we're on a mobile platform
const isMobilePlatform = () => {
  return Capacitor.isNativePlatform();
};

// Secure storage helpers for mobile
const saveRefreshTokenToStorage = async (token) => {
  if (!isMobilePlatform()) return;
  try {
    await Preferences.set({ key: 'refreshToken', value: token });
    console.log('[AuthContext] Saved refresh token to secure storage');
  } catch (error) {
    console.error('[AuthContext] Failed to save refresh token to storage:', error);
  }
};

const getRefreshTokenFromStorage = async () => {
  if (!isMobilePlatform()) return null;
  try {
    const { value } = await Preferences.get({ key: 'refreshToken' });
    return value;
  } catch (error) {
    console.error('[AuthContext] Failed to get refresh token from storage:', error);
    return null;
  }
};

const clearRefreshTokenFromStorage = async () => {
  if (!isMobilePlatform()) return;
  try {
    await Preferences.remove({ key: 'refreshToken' });
    console.log('[AuthContext] Cleared refresh token from secure storage');
  } catch (error) {
    console.error('[AuthContext] Failed to clear refresh token from storage:', error);
  }
};

export const AuthProvider = ({ children }) => {
  const [accessToken, setAccessTokenState] = useState(null);
  const [userId, setUserId] = useState(null);
  const [initialized, setInitialized] = useState(false);

  // Refresh promise to prevent concurrent refresh attempts
  const refreshPromiseRef = useRef(null);

  // ─── Simple logout function
  const logout = useCallback(async () => {
    setAxiosToken(null);
    setAccessTokenState(null);
    accessTokenRef.current = null;
    setUserId(null);
    
    // Clear any ongoing refresh promise
    refreshPromiseRef.current = null;
    
    // Clear refresh token from mobile storage
    await clearRefreshTokenFromStorage();
    
    // Call logout endpoint (non-blocking)
    axiosInstance.post('/auth/logout').catch(() => {
      console.warn('Logout endpoint failed, but local state cleared');
    });
  }, []);

  // ─── Serialized token refresh function - Prevents concurrent refresh attempts
  const refreshToken = useCallback(async () => {
    // If a refresh is already in progress, wait for it instead of starting a new one
    if (refreshPromiseRef.current) {
      console.log('[AuthContext] Refresh already in progress, waiting for existing promise');
      return refreshPromiseRef.current;
    }

    // Start new refresh and store the promise
    refreshPromiseRef.current = (async () => {
      try {
        console.log('[AuthContext] refreshToken() called - starting new refresh');
        await initializeCsrf();
        console.log('[AuthContext] CSRF initialized, calling /auth/refresh');
        
        let res;
        const requestConfig = { withCredentials: true };
        
        // For mobile platforms, send refresh token in Authorization header
        if (isMobilePlatform()) {
          const storedRefreshToken = await getRefreshTokenFromStorage();
          if (storedRefreshToken) {
            console.log('[AuthContext] Mobile platform - using stored refresh token');
            requestConfig.headers = {
              'Authorization': `Bearer ${storedRefreshToken}`,
              'X-Client-Platform': 'mobile'
            };
          } else {
            console.warn('[AuthContext] Mobile platform but no stored refresh token');
            await logout();
            return null;
          }
        }
        
        try {
          res = await axiosInstance.post('/auth/refresh', {}, requestConfig);
        } catch (err) {
          const status = err?.response?.status;
          const transient = !status || status === 429 || status === 503 || (status >= 500 && status < 600);
          if (transient) {
            const delay = 500 + Math.floor(Math.random() * 500);
            console.warn(`[AuthContext] transient refresh error ${status || err?.code || 'network'} — retrying in ${delay}ms`);
            await new Promise(r => setTimeout(r, delay));
            // one retry
            res = await axiosInstance.post('/auth/refresh', {}, requestConfig);
          } else {
            throw err;
          }
        }
        
        const newToken = res.data.accessToken;
        
        // Save new refresh token to mobile storage if provided
        if (isMobilePlatform() && res.data.refreshToken) {
          await saveRefreshTokenToStorage(res.data.refreshToken);
        }
        
        // Update all token references
        setAxiosToken(newToken);
        setAccessTokenState(newToken);
        accessTokenRef.current = newToken;
        
        // Update userId from token
        try {
          const decoded = jwtDecode(newToken);
          setUserId(decoded.user_id);
        } catch (decodeError) {
          console.warn('Failed to decode token:', decodeError);
        }
        
        console.log('[AuthContext] Token refresh successful, new token set');
        return newToken;
      } catch (error) {
        const status = error?.response?.status;
        console.warn('[AuthContext] Token refresh failed:', status, error?.message || error);
        // Only logout on definitive auth failure (invalid/expired refresh)
        if (status === 401 || status === 400) {
          await logout();
        } else {
          // Transient error: keep session cookies; allow caller to decide/retry later
        }
        return null;
      } finally {
        // Clear the promise so future refreshes can proceed
        refreshPromiseRef.current = null;
      }
    })();

    return refreshPromiseRef.current;
  }, [logout]);

  // ─── Initial token load ONCE on app start
  const loadAccessToken = async () => {
    if (accessTokenRef.current) {
      setAccessTokenState(accessTokenRef.current);
      setInitialized(true);
      return;
    }

    try {
      const token = await refreshToken();
      if (!token) {
        console.log('No valid session found on startup');
      }
    } catch (err) {
      console.warn('No valid session found on startup:', err);
    } finally {
      setInitialized(true);
    }
  };

  // ─── Run bootstrap ONCE on mount
  useEffect(() => {
    // Register our refresh function with axios interceptor
    console.log('[AuthContext] Registering refresh function with axiosInstance');
    setRefreshFunction(refreshToken);

    loadAccessToken();
  }, []);

  // ─── Listen for auth failures from axios interceptor
  useEffect(() => {
    const handleAuthFailed = () => {
      console.log('Auth failed event received, clearing tokens...');
      logout();
    };

    window.addEventListener('auth-failed', handleAuthFailed);
    return () => window.removeEventListener('auth-failed', handleAuthFailed);
  }, []);

  // ─── Expose context values
  const contextValue = {
    accessToken,
    setAccessToken: (token) => {
      console.log('AuthContext: Setting new access token');
      setAxiosToken(token);
      setAccessTokenState(token);
      accessTokenRef.current = token;
      // Decode userId from token
      if (token) {
        try {
          const decoded = jwtDecode(token);
          setUserId(decoded.user_id);
          console.log('AuthContext: Token updated for user:', decoded.user_id);
        } catch (decodeError) {
          console.warn('Failed to decode token:', decodeError);
        }
      } else {
        setUserId(null);
        console.log('AuthContext: Token cleared');
      }
    },
    userId,
    initialized,
    refreshToken,
    logout,
  };

  // ─── Simple loading screen
  if (!initialized) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '18px',
        color: '#666'
      }}>
      </div>
    );
  }

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
